import {Card, Container, Grid, Group, Stack, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {
  Badge,
  CompanySelect,
  ErrorPage,
  MonthPickerInput,
  PipelineFlowTransportDataTable,
  ResizeableContent,
  StatisticsDataTable,
  ThemedPage,
  useApp,
  useAuth,
} from "@ots-frontend/ots-ui";
import {IconCalendar, IconCoins} from "@tabler/icons-react";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useCallback, useMemo, useState} from "react";
import {
  addMonths,
  adjustToTimeZone,
  CompanyEntity,
  EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
  firstOrUndefined,
  flowAllocationType,
  flowRawStatisticsSelector,
  transportMethod,
  StatisticsSearchCriteria,
  statisticsSelector,
  statisticsType,
  timeGroup,
  useSearchStatisticsQuery
} from "@ots-frontend/ots-data";
import {useDispatch} from "react-redux";
import {getPipelineSettlementPageCriteria} from "../../store/pipeline-settlement-page/selectors";
import {setPipelineSettlementPageCriteria} from "../../store/pipeline-settlement-page/actions";
import {skipToken} from "@reduxjs/toolkit/query";

export type PipelineSettlementPageProps = {}

export const PipelineSettlementPage = (props: PipelineSettlementPageProps) => {

  const criteria = useAppSelector(getPipelineSettlementPageCriteria)

  const [companyId, setCompanyId] = useState<string | undefined>(criteria?.companyId)
  const [companyIdError, setCompanyIdError] = useState<boolean>(false)

  const {t} = useTranslation(["translation", "common"]);
  const dispatch = useDispatch()
  const {regionId, appCategory} = useApp()
  const {hasAccess} = useAuth()

  const searchCriteria: StatisticsSearchCriteria | typeof skipToken = useMemo(() => {
    if (companyId) {
      return {
        fromDttm: adjustToTimeZone(criteria.dttm).getTime(),
        toDttm: adjustToTimeZone(addMonths(criteria.dttm, 1)).getTime(),
        regionIds: regionId ? [regionId] : undefined,
        categories: appCategory ? [appCategory.code] : undefined,
        includeSubTruckCompanies: true,
        includeTruckingData: false,
        includeContainerVolumeInventoryData: false,
        includePipelineData: true,
        pipelineCompanyIds: [companyId],
        pipelineFlowTimeGroup: timeGroup.monthly.code,
        pipelineFlowAllocationType: flowAllocationType.sourceSite.code,
      }
    } else {
      return skipToken
    }
  }, [companyId, criteria.dttm, regionId, appCategory])

  const selectStatistics = useMemo(() => {
    return statisticsSelector()
  }, []);

  const selectFlows = useMemo(() => {
    return flowRawStatisticsSelector()
  }, []);

  const {statisticsBundle, flows, isLoading} = useSearchStatisticsQuery(searchCriteria, {
    selectFromResult: (result) => ({
      statisticsBundle: selectStatistics(result, {
        ...EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
        processSite: true,
        processTruckCompany: true,
        processVendorCompany: true,
      }),
      flows: selectFlows(result),
      isLoading: result.isLoading || result.isFetching,
    })
  })

  const onDateChange = useCallback((date: Date) => {
    console.log(date)
    dispatch(setPipelineSettlementPageCriteria({
      ...criteria,
      dttm: date.getTime(),
    }));
  }, [dispatch, criteria]);

  const onCompanyChange = useCallback((values: CompanyEntity[]) => {
    const value = firstOrUndefined(values)
    dispatch(setPipelineSettlementPageCriteria({
      ...criteria,
      companyId: value ? value?.id : undefined,
    }))

    setCompanyId(value?.id)
  }, [dispatch, criteria])

  if (!hasAccess("view_cost")) {
    return <ErrorPage/>
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container
              w={size.width} h={size.height}
              className={classes.container}
              style={{"overflowY": "scroll"}}
              fluid={true}
              p="md">
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <Group position="apart">
                  <Group spacing="xs">
                    <IconCoins/>
                    <Title order={4}>
                      {t("page.pipelinesettlement.title", "Pipeline Settlement", {ns: "translation"})}
                    </Title>
                    <Badge color="blue">
                      Beta
                    </Badge>
                  </Group>
                </Group>

                <Group position="center" spacing="xs">
                  <IconCalendar size="1.1rem" stroke={1.5}/>
                  <MonthPickerInput
                    defaultValue={new Date(criteria.dttm)}
                    onChange={onDateChange}
                    popoverProps={{
                      withinPortal: true
                    }}/>
                  <CompanySelect
                    placeholder={"Company"}
                    withinPortal={true}
                    selector={{
                      regionId: regionId
                    }}
                    onEntityChange={onCompanyChange}
                    defaultValue={companyId}/>
                </Group>
              </Card>
              <Grid>
                <Grid.Col span={12}>
                  <Card shadow="sm" padding="lg" mb="lg" radius="md">
                    <Stack>
                      <Title order={3}>
                        Settlement
                      </Title>
                      <PipelineFlowTransportDataTable
                        withBorder
                        scrollAreaProps={{
                          offsetScrollbars: true
                        }}
                        records={flows}
                        recordsPerPageOptions={[10]}
                        fetching={isLoading}/>
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Card shadow="sm" padding="lg" mb="lg" radius="md">
                    <Stack>
                      <Title order={3}>
                        Source
                      </Title>
                      <StatisticsDataTable
                        withBorder
                        scrollAreaProps={{
                          offsetScrollbars: true
                        }}
                        method={transportMethod.pipeline}
                        types={[
                          statisticsType.quantity,
                          statisticsType.standardQuantity,
                          statisticsType.netQuantity,
                          statisticsType.billableQuantity,
                          statisticsType.cost
                        ]}
                        category={appCategory}
                        records={statisticsBundle?.pickupSiteStats}
                        recordsPerPageOptions={[10]}
                        fetching={isLoading}
                        hideExpansion={true}
                        showTotals={true}/>
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Card shadow="sm" padding="lg" mb="lg" radius="md">
                    <Stack>
                      <Title order={3}>
                        Destination
                      </Title>
                      <StatisticsDataTable
                        withBorder
                        scrollAreaProps={{
                          offsetScrollbars: true
                        }}
                        method={transportMethod.pipeline}
                        types={[
                          statisticsType.quantity,
                          statisticsType.standardQuantity,
                          statisticsType.netQuantity,
                          statisticsType.billableQuantity,
                          statisticsType.cost
                        ]}
                        category={appCategory}
                        records={statisticsBundle?.deliverySiteStats}
                        recordsPerPageOptions={[10]}
                        fetching={isLoading}
                        hideExpansion={true}
                        showTotals={true}/>
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
